/**
 * Los datos que faltan para poder facturar, y el formulario para meterlos.
 *
 * Se usa desde DOS sitios:
 *   - el CRM, para que el comercial vea qué pedirle al cliente
 *   - Presupuestos, para que quien emite las facturas lo complete
 *
 * Es el mismo componente y el mismo endpoint en los dos, y eso es lo que hace
 * que estén sincronizados. Con un formulario por pantalla habría que mantener
 * la sincronización; con uno solo, no puede romperse.
 *
 * El estado ("qué falta") tampoco se calcula aquí: lo calcula el servidor y lo
 * guarda en la ficha. Esta pantalla solo lo pinta.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

/** Los siete campos, en el orden en que se piden por teléfono. */
export const CAMPOS = [
  { clave: 'fiscal_name', etiqueta: 'Nombre fiscal', ancho: 2 },
  { clave: 'tax_id', etiqueta: 'NIF / CIF' },
  // `multiple` deja escribir varias separadas por coma; sin el, el
  // navegador da por invalida la lista y no deja guardar.
  { clave: 'billing_email', etiqueta: 'Email de facturación', tipo: 'email',
    multiple: true, ayuda: 'Varios separados por coma llegan a todos' },
  { clave: 'address', etiqueta: 'Dirección', ancho: 2 },
  { clave: 'postal_code', etiqueta: 'Código postal' },
  { clave: 'city', etiqueta: 'Ciudad' },
  { clave: 'province', etiqueta: 'Provincia' },
  // Sale en la factura junto a la provincia. La ficha lo tenia ("España"
  // por defecto) pero este formulario no lo enseñaba.
  { clave: 'country', etiqueta: 'País' },
];

/**
 * ¿El código postal es de un territorio fuera del IVA?
 *
 * Canarias (35, 38), Ceuta (51) y Melilla (52). Solo sirve para AVISAR: la
 * decisión la toma una persona y se guarda en la ficha. El backend nunca
 * deduce el impuesto del código postal al facturar, porque entonces corregir
 * un CP cambiaría el importe de las facturas siguientes en silencio.
 */
export function sugiereFueraDelIVA(datos) {
  if (!datos || datos.tax_type) return false;
  const cp = String(datos.postal_code || '').replace(/\D/g, '');
  if (cp.length < 4) return false;
  return ['35', '38', '51', '52'].includes(cp.padStart(5, '0').slice(0, 2));
}

/**
 * Aviso compacto de que faltan datos. Va en la tarjeta del CRM y en la línea
 * del presupuesto.
 */
export function AvisoFaltanDatos({ estado, onAbrir, compacto = false }) {
  if (!estado) return null;

  if (estado.billing_ready) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700"
            data-testid="datos-completos">
        <CheckCircle2 className="w-3 h-3" />
        {compacto ? 'Listo' : 'Datos completos: se puede facturar'}
      </span>
    );
  }

  const n = (estado.billing_missing || []).length;

  // "Faltan 0 datos" es la peor frase posible al lado de un botón bloqueado:
  // dice que no falta nada y aun así no deja seguir. Pasaba cuando la ficha
  // todavía no se había evaluado y la lista llegaba vacía. Mientras no se
  // sepa qué falta, se invita a abrir el formulario, que sí lo calcula.
  const texto = n === 0
    ? (compacto ? 'Revisar datos' : 'Faltan datos para facturar: revísalos aquí')
    : (compacto ? `Faltan ${n}` : `Faltan ${n} ${n === 1 ? 'dato' : 'datos'} para facturar`);

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onAbrir?.(); }}
      className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 hover:underline text-left"
      title={n ? (estado.billing_missing || []).join(', ')
               : 'Abre el formulario para ver qué falta'}
      data-testid="aviso-faltan-datos"
    >
      <AlertTriangle className="w-3 h-3 shrink-0" />
      {texto}
    </button>
  );
}

/**
 * Formulario de datos fiscales.
 *
 * A nivel de módulo, como todo lo que tiene campos de texto: definido dentro
 * de un padre se remonta en cada render y el input pierde el foco en cada
 * tecla.
 */
export function DialogoDatosFacturacion({ open, onOpenChange, customerId, nombre, onGuardado }) {
  const [datos, setDatos] = useState({});
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    if (!customerId) return;
    setCargando(true);
    setError(null);
    try {
      const [est, cli] = await Promise.all([
        axiosInstance.get(`/leads/${customerId}/billing-status`),
        axiosInstance.get(`/customers/${customerId}`).catch(() => ({ data: {} })),
      ]);
      setEstado(est.data);
      const c = cli.data || {};
      setDatos({
        fiscal_name: c.fiscal_name || '',
        tax_id: c.tax_id || '',
        billing_email: c.billing_email || '',
        address: c.address || '',
        postal_code: c.postal_code || '',
        city: c.city || '',
        province: c.province || '',
        country: c.country || '',
        sepa_iban: c.sepa_iban || '',
        collection_method: est.data.billing_collection_method || '',
        tax_type: c.tax_type || '',
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron cargar los datos');
    } finally {
      setCargando(false);
    }
  }, [customerId]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const campo = (k) => (e) => setDatos((d) => ({ ...d, [k]: e.target.value }));

  const guardar = async () => {
    setGuardando(true);
    try {
      // Se manda solo lo que tiene valor: el endpoint guarda a trozos porque
      // el NIF llega por teléfono y la dirección al día siguiente por email.
      const payload = Object.fromEntries(
        Object.entries(datos).filter(([, v]) => v !== '' && v !== null));

      const { data } = await axiosInstance.post(`/leads/${customerId}/billing-data`, payload);
      setEstado((e) => ({ ...e, ...data }));

      if (data.billing_ready && data.es_cliente) {
        toast.success('Datos completos. Ya es cliente y se le puede facturar.');
        onOpenChange(false);
      } else if (data.billing_ready) {
        toast.success('Datos completos. Pasará a cliente cuando conste el cobro.');
        onOpenChange(false);
      } else {
        toast.info(`Guardado. Todavía faltan: ${data.billing_missing.join(', ')}`);
      }
      onGuardado?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron guardar los datos');
    } finally {
      setGuardando(false);
    }
  };

  const falta = (clave) => (estado?.billing_missing_fields || []).includes(clave);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="dialogo-datos-facturacion">
        <DialogHeader>
          <DialogTitle>Datos para facturar{nombre ? `: ${nombre}` : ''}</DialogTitle>
          <DialogDescription>
            Hasta que estén todos, no se puede emitir la factura. Se puede
            guardar a trozos: lo que vayas teniendo.
          </DialogDescription>
        </DialogHeader>

        {cargando && <p className="text-slate-500 py-6 text-center">Cargando...</p>}

        {error && (
          <div className="border border-red-200 bg-red-50 rounded p-3">
            <p className="text-red-800 text-sm">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={cargar}>Reintentar</Button>
          </div>
        )}

        {!cargando && !error && (
          <>
            {estado && !estado.billing_ready && (
              <div className="border border-amber-200 bg-amber-50 rounded p-3">
                <p className="text-amber-900 text-sm font-medium mb-1">
                  Falta por conseguir:
                </p>
                <div className="flex flex-wrap gap-1">
                  {estado.billing_missing.map((m) => (
                    <Badge key={m} className="bg-amber-100 text-amber-900">{m}</Badge>
                  ))}
                </div>
              </div>
            )}

            {estado?.billing_ready && (
              <div className="border border-green-200 bg-green-50 rounded p-3">
                <p className="text-green-900 text-sm font-medium">
                  Todo listo. Ya se le puede facturar.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {CAMPOS.map(({ clave, etiqueta, tipo, ancho, multiple, ayuda }) => (
                <div key={clave} className={`space-y-1.5 ${ancho === 2 ? 'col-span-2' : ''}`}>
                  <Label htmlFor={clave} className={falta(clave) ? 'text-amber-800 font-medium' : ''}>
                    {etiqueta}{falta(clave) && ' *'}
                  </Label>
                  <Input
                    id={clave} type={tipo || 'text'} multiple={multiple}
                    value={datos[clave] || ''} onChange={campo(clave)}
                    className={falta(clave) ? 'border-amber-400' : ''}
                    data-testid={`campo-${clave}`}
                  />
                  {ayuda && <p className="text-xs text-slate-500">{ayuda}</p>}
                </div>
              ))}
            </div>

            <div className="border-t pt-3 space-y-1.5">
              <Label htmlFor="tax_type">Impuesto de sus facturas</Label>
              <select
                id="tax_type" value={datos.tax_type || ''}
                onChange={campo('tax_type')}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                data-testid="campo-tax_type"
              >
                <option value="">IVA general (21%)</option>
                <option value="NO_SUJETO">No sujeta a IVA (Canarias, Ceuta, Melilla)</option>
                <option value="IGIC">IGIC (Canarias)</option>
                <option value="IPSI">IPSI (Ceuta y Melilla)</option>
              </select>
              {/* El aviso solo se enseña; no cambia nada solo. Deducir el
                  impuesto del código postal al facturar sería más cómodo,
                  pero entonces corregir un CP cambiaría el importe de las
                  facturas siguientes sin que nadie lo hubiera decidido. */}
              {sugiereFueraDelIVA(datos) && (
                <p className="text-xs text-amber-700 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                  El código postal {datos.postal_code} es de un territorio fuera
                  del IVA. Si es correcto, marca "No sujeta".
                </p>
              )}
              <p className="text-xs text-slate-500">
                Se aplica a todas sus facturas, también a las repetitivas que
                genera el sistema cada mes.
              </p>
            </div>

            {estado?.billing_has_recurring && (
              <div className="border-t pt-3 space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="collection_method"
                         className={falta('collection_method') ? 'text-amber-800 font-medium' : ''}>
                    Cómo se cobra la cuota{falta('collection_method') && ' *'}
                  </Label>
                  {/* Ya no hay opcion "Sin decidir": sin elegir nada se cobra
                      por recibo, asi que ofrecerla era ofrecer un estado que
                      no existe. Ademas guardarla no hacia nada -- el endpoint
                      solo escribe si viene un valor -- y sin embargo bloqueaba
                      la facturacion. */}
                  <select
                    id="collection_method" value={datos.collection_method || 'recibo'}
                    onChange={campo('collection_method')}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                    data-testid="campo-collection_method"
                  >
                    <option value="recibo">Recibo domiciliado (SEPA)</option>
                    <option value="transferencia">Transferencia</option>
                  </select>
                  <p className="text-xs text-slate-500">
                    Este presupuesto lleva cuota recurrente. Por defecto se cobra
                    por recibo, y entonces hace falta el IBAN; por transferencia,
                    no.
                  </p>
                </div>

                {datos.collection_method === 'recibo' && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5 col-span-2">
                      <Label htmlFor="sepa_iban"
                             className={falta('sepa_iban') ? 'text-amber-800 font-medium' : ''}>
                        IBAN{falta('sepa_iban') && ' *'}
                      </Label>
                      <Input id="sepa_iban" value={datos.sepa_iban || ''}
                             onChange={campo('sepa_iban')}
                             placeholder="ES91 2100 0418 4502 0005 1332"
                             data-testid="campo-sepa_iban" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="sepa_bic">BIC (opcional)</Label>
                      <Input id="sepa_bic" value={datos.sepa_bic || ''}
                             onChange={campo('sepa_bic')} data-testid="campo-sepa_bic" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          <Button onClick={guardar} disabled={guardando || cargando}
                  className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                  data-testid="guardar-datos-facturacion">
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
